import type { FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { useMap } from "react-leaflet"
import {
  Box,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import CloseIcon from "@mui/icons-material/Close"

import type { EntityType } from "types/common.types"
import type { ContextDevice } from "types/device.types"

import { nameOrUnnamed } from "helpers/utils/translations"
import EntityIcon from "widgets/common/EntityIcon"
import DevicesSection from "./DevicesSection"
import FieldValue, { Value } from "./FieldValue"
import Row from "./Row"
import { isFieldEmpty, isMultiline, type EntityPopupField } from "./helpers"

export type { EntityPopupField } from "./helpers"

interface EntityPopupProps {
  entity: EntityType
  name: string | null | undefined
  fields: EntityPopupField[]
  id: number
  /** The devices installed on the entity, listed after the fields. */
  devices?: { items: ContextDevice[]; loading?: boolean }
  /** Where the name and the action link to. Without it the card links nowhere. */
  href?: string
  /** Blocks between the list and the action, such as the span's notices. */
  children?: ReactNode
}

/**
 * What a map popup shows, drawn as an entity card: the entity and its name, one
 * row per field, the ID and the way to the entity's page.
 */
const EntityPopup: FC<EntityPopupProps> = ({
  entity,
  name,
  fields,
  id,
  devices,
  href,
  children,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"
  const map = useMap()

  const nameSx = { overflowWrap: "anywhere", fontStyle: name ? "normal" : "italic" }

  return (
    <Box padding={2}>
      <Stack divider={<Divider />}>
        <Stack direction="row" alignItems="flex-start" gap={1.5} paddingBottom={1.5}>
          <EntityIcon entity={entity} variant="avatar" avatarSize={36} size={20} />
          <Stack flex={1} minWidth={0}>
            <Typography variant="caption" color="text.secondary">
              {t(`entities.${entity.toUpperCase()}`)}
            </Typography>
            {href ? (
              <Link to={href} style={{ textDecoration: "none" }}>
                <Typography
                  variant="h4"
                  sx={{
                    ...nameSx,
                    color: "text.primary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {nameOrUnnamed(name, t)}
                </Typography>
              </Link>
            ) : (
              <Typography variant="h4" sx={nameSx}>
                {nameOrUnnamed(name, t)}
              </Typography>
            )}
          </Stack>
          <IconButton
            aria-label={t("geo_info.CLOSE")}
            onClick={() => map.closePopup()}
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              marginTop: -0.5,
              marginRight: -1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {fields
          .filter((field) => !isFieldEmpty(field))
          .map((field) => (
            <Row key={field.label} label={field.label} alignTop={isMultiline(field)}>
              <FieldValue field={field} locale={locale} />
            </Row>
          ))}
        {/* Left out when empty here rather than inside, so no divider is drawn for it. */}
        {devices?.loading ? (
          <Stack gap={1} paddingY={1.25}>
            <Skeleton width={90} />
            <Skeleton width={180} />
          </Stack>
        ) : (
          !!devices?.items.length && <DevicesSection devices={devices.items} />
        )}
        <Row label={t("geo_info.ID")}>
          <Value>{id}</Value>
        </Row>
      </Stack>
      {children}
      {href && (
        <Box display="flex" justifyContent="flex-end" marginTop={1}>
          <Button
            component={Link}
            to={href}
            size="small"
            endIcon={<ArrowForwardIcon />}
            sx={{ fontWeight: 600 }}
          >
            {t(`geo_info.GO_TO_${entity.toUpperCase()}`)}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default EntityPopup
